import { OfferUI } from 'common/contracts';
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROMOTION_DEFAULTS,
  ROLLING_OFFER_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { InitialValuesBuilder, OfferDesignFormValues, OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { formatName, sanitizeExternalId } from 'views/OfferDesign/OfferDesign.utils';

type OfferData = OfferUI | undefined;

const buildBackgroundColor = (data: OfferData, offerType: OfferDesignTypes) => {
  if (offerType === OfferDesignTypes.Bundle) return data?.backgroundColor;
  if (offerType === OfferDesignTypes.SpecialOffer || offerType === OfferDesignTypes.PopUp) {
    return data?.specialOffer?.backgroundColor;
  }
  return data?.rollingOffer?.backgroundColor;
};

const buildBaseInitialValues = (data: OfferData, offerType: OfferDesignTypes, isDup: boolean) => ({
  name: formatName(data?.name, isDup),
  externalId: sanitizeExternalId(data?.externalId),
  backgroundImage: data?.backgroundImage || '',
  backgroundColor:
    buildBackgroundColor(data, offerType) ||
    (offerType === OfferDesignTypes.SpecialOffer || offerType === OfferDesignTypes.PopUp
      ? POPUP_DEFAULTS.backgroundColor
      : ''),
  buttonSuffixImage: data?.buttonSuffixImage || '',
  buttonSuffixAnimation: data?.buttonSuffixAnimation || '',
  borderColor: data?.borderColor || DEFAULT_BORDER_COLOR,
  borderWidth: data?.borderWidth ?? DEFAULT_BORDER_WIDTH,
  offerUiType: offerType
});

export const buildPromotionInitialValues: InitialValuesBuilder = (data) => ({
  collectText: data?.specialOffer?.collectText || PROMOTION_DEFAULTS.collectText,
  title: data?.specialOffer?.title || '',
  fontSize: data?.specialOffer?.fontSize || PROMOTION_DEFAULTS.fontSize,
  fontWeight: data?.specialOffer?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
  fontColor: data?.specialOffer?.fontColor || PROMOTION_DEFAULTS.fontColor,
  subTitleText: data?.specialOffer?.subTitle?.text || '',
  subTitleFontSize: data?.specialOffer?.subTitle?.fontSize || PROMOTION_DEFAULTS.subFontSize,
  subTitleFontWeight: data?.specialOffer?.subTitle?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
  subTitleFontColor: data?.specialOffer?.subTitle?.fontColor || PROMOTION_DEFAULTS.fontColor
});

export const buildProgressBarInitialValues: InitialValuesBuilder = (data) => ({
  backgroundOpacity: data?.progressBar?.backgroundOpacity ?? PROGRESS_BAR_DEFAULTS.backgroundOpacity,
  frameRadius: data?.progressBar?.frameRadius ?? PROGRESS_BAR_DEFAULTS.frameRadius,
  barFillColor: data?.progressBar?.barFillColor || PROGRESS_BAR_DEFAULTS.barFillColor,
  barBackgroundColor: data?.progressBar?.backgroundColor || PROGRESS_BAR_DEFAULTS.backgroundColor,
  barRadius: data?.progressBar?.barRadius ?? PROGRESS_BAR_DEFAULTS.barRadius,
  borderColor: data?.borderColor || PROGRESS_BAR_DEFAULTS.borderColor,
  borderWidth: data?.borderWidth ?? PROGRESS_BAR_DEFAULTS.borderWidth,
  accumulatedItemImageUrl: data?.progressBar?.accumulatedItem?.imageUrl || '',
  accumulatedItemBackgroundImageUrl: data?.progressBar?.accumulatedItem?.backgroundImageUrl || PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundImageUrl,
  textColor: data?.progressBar?.textColor || PROGRESS_BAR_DEFAULTS.textColor,
  barBackgroundImage: data?.progressBar?.barBackgroundImage || '',
  headerImage: data?.progressBar?.headerImage || ''
});

export const buildPopupInitialValues: InitialValuesBuilder = (data, offerSubType) => ({
  badgeCoverImage: data?.badgeCoverImage || '',
  title: data?.specialOffer?.title || '',
  fontSize: data?.specialOffer?.fontSize || POPUP_DEFAULTS.fontSize,
  fontWeight: data?.specialOffer?.fontWeight || POPUP_DEFAULTS.fontWeight,
  fontColor: data?.specialOffer?.fontColor || POPUP_DEFAULTS.fontColor,
  borderColor: data?.borderColor || POPUP_DEFAULTS.borderColor,
  borderWidth: data?.borderWidth ?? POPUP_DEFAULTS.borderWidth,
  offerUiSubType: data?.offerUiSubType || offerSubType || 'DailyBonus'
});

export const buildRollingOfferInitialValues: InitialValuesBuilder = (data) => ({
  collectText: data?.rollingOffer?.subRollingOffer?.collectText || ROLLING_OFFER_DEFAULTS.collectText,
  backgroundOpacity: data?.rollingOffer?.backgroundOpacity ?? ROLLING_OFFER_DEFAULTS.backgroundOpacity,
  arrowColor: data?.rollingOffer?.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor,
  arrowImage: data?.rollingOffer?.arrowImage || '',
  headerImage: data?.rollingOffer?.headerImage || '',
  enableScroll: Boolean(data?.rollingOffer?.enableScroll),
  scrollArrowImage: data?.rollingOffer?.scrollArrowImage || '',
  horizontalBackgroundImage: data?.horizontalBackgroundImage || '',
  offersBackgroundColor:
    data?.rollingOffer?.subRollingOffer?.backgroundColor ||
    ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
  offersBackgroundImage: data?.rollingOffer?.subRollingOffer?.backgroundImage || '',
  borderColor: data?.borderColor || ROLLING_OFFER_DEFAULTS.borderColor,
  borderWidth: data?.borderWidth ?? ROLLING_OFFER_DEFAULTS.borderWidth
});

export const buildInitialValues = (
  offerType: OfferDesignTypes,
  data: OfferData,
  isDup: boolean,
  offerSubType: string,
  typeBuilders: Record<OfferDesignTypes, InitialValuesBuilder | undefined>
): OfferDesignFormValues => ({
  ...buildBaseInitialValues(data, offerType, isDup),
  ...typeBuilders[offerType]?.(data, offerSubType)
} as OfferDesignFormValues);
